import {
  CalendarDate,
  getLocalTimeZone,
  today,
  parseDate
} from '@internationalized/date';
import { z } from 'zod';

/**
 * Generic date filter supporting common operators and pluggable preset keys.
 * Useful when different domains need different preset vocabularies/resolvers.
 */
export type GenericDateFilter<Preset extends string> =
  | {
      operator: 'equal';
      value: CalendarDate;
    }
  | {
      operator: 'greaterThan'; // "after"
      value: CalendarDate;
    }
  | {
      operator: 'lessThan'; // "before"
      value: CalendarDate;
    }
  | {
      operator: 'between';
      value: {
        from: CalendarDate;
        to: CalendarDate;
      };
    }
  | {
      operator: 'preset';
      value: Preset;
    };

export type DateFilterResolver<Preset extends string> = {
  match: (
    filter: GenericDateFilter<Preset>,
    date: CalendarDate,
    now?: CalendarDate
  ) => boolean;
};

export type PresetConfig = {
  match: (date: CalendarDate, now: CalendarDate) => boolean;
};

/**
 * Factory that produces a date-filter resolver with generic preset handling.
 */
export function createDateFilter<P extends string>(
  presetMap: Record<P, PresetConfig>
): DateFilterResolver<P> {
  function match(
    filter: GenericDateFilter<P>,
    date: CalendarDate,
    nowArg?: CalendarDate
  ) {
    const now = nowArg ?? today(getLocalTimeZone());
    switch (filter.operator) {
      case 'equal':
        return date.compare(filter.value) === 0;
      case 'greaterThan':
        return date.compare(filter.value) > 0;
      case 'lessThan':
        return date.compare(filter.value) < 0;
      case 'between': {
        const from = filter.value.from;
        const to = filter.value.to;
        return date.compare(from) >= 0 && date.compare(to) <= 0;
      }
      case 'preset': {
        const preset = presetMap[filter.value];
        return preset ? preset.match(date, now) : false;
      }
    }
  }

  return {
    match
  };
}

/**
 * Helper to create a zod schema for GenericDateFilter using the provided preset ids.
 * Note: requires at least one preset id.
 */
const isCalendarDate = (value: unknown) =>
  value instanceof CalendarDate ||
  (typeof value === 'object' &&
    value !== null &&
    'year' in value &&
    'month' in value &&
    'day' in value &&
    typeof value.year === 'number' &&
    typeof value.month === 'number' &&
    typeof value.day === 'number');

export function dateFilterSchemaOf<P extends string>(presetIds: readonly P[]) {
  const presetEnum = (presetIds.length > 0
    ? z.enum(presetIds as [P, ...P[]])
    : // when no presets, create an any enum to keep schema valid
      z.any()) as unknown as z.ZodType<P>;

  const calendarDate = z
    .union([
      z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .transform(parseDate),
      z.custom(isCalendarDate)
    ])
    .transform((value): CalendarDate => {
      if (value instanceof CalendarDate) {
        return value;
      }
      // Plain object - reconstruct CalendarDate
      const { year, month, day } = value as {
        year: number;
        month: number;
        day: number;
      };
      return new CalendarDate(year, month, day);
    });

  return z.discriminatedUnion('operator', [
    z.object({
      operator: z.literal('equal'),
      value: calendarDate
    }),
    z.object({
      operator: z.literal('greaterThan'),
      value: calendarDate
    }),
    z.object({
      operator: z.literal('lessThan'),
      value: calendarDate
    }),
    z.object({
      operator: z.literal('between'),
      value: z.object({
        from: calendarDate,
        to: calendarDate
      })
    }),
    z.object({
      operator: z.literal('preset'),
      value: presetEnum
    })
  ]);
}
